import { useNavigate, useParams } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  CardInfo,
  Icon,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { ContractVersionsPageContractVersionRequestFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'

type Props = {
  contractVersionRequest: ContractVersionsPageContractVersionRequestFragment
  employeeName: string
}

const getTitle = ({
  actionType,
  requesterName,
  employeeName,
}: {
  actionType: string
  requesterName: string
  employeeName: string
}) => {
  switch (actionType) {
    case FLOWS.promotion:
      return i18n.t('contract_versions.approval_banner.promotion.title', {
        requesterName: requesterName,
        employeeName: employeeName,
      })
    case FLOWS.evolution:
      return i18n.t('contract_versions.approval_banner.evolution.title', {
        requesterName: requesterName,
        employeeName: employeeName,
      })
  }
}

const ApprovalBanner = ({ contractVersionRequest, employeeName }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const params = useParams()

  const requester = contractVersionRequest.approvalAuthor
  const requestedAt = contractVersionRequest.approvalRequestCreatedAt || ''

  if (!requester || !contractVersionRequest.id) return null

  const approvalActionType = contractVersionRequest.approvalActionType || ''

  const navigateToApproval = () => {
    if (!contractVersionRequest.id) return

    track(trackingEvents.employee.contracts.goToContractRequest, {
      source: 'contract_page',
      approval_status: contractVersionRequest.status,
      request_type: approvalActionType,
      request_date: requestedAt,
      request_reason: contractVersionRequest.requestDetails,
    })
    navigate(
      navigation.employees.id.contractVersions.approval_request.fullPath({
        id: String(params.id),
        contractVersionId: contractVersionRequest.id,
      })
    )
  }

  const title = getTitle({
    actionType: approvalActionType,
    requesterName: requester.fullName ?? '',
    employeeName: employeeName,
  })

  return (
    <Box style={{ marginBottom: '24px' }}>
      <CardInfo>
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box alignItems="flexStart" flexDirection="row" gap="s12">
            <Avatar
              size="s24"
              url={buildUrl(requester.avatar?.url)}
              label={requester.fullName ?? ''}
            />
            <Box flexDirection="column" gap="s12">
              <Text size={'200'} weight="semibold">
                {title}
              </Text>
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Tag type="champagne">{i18n.t('contract_versions.approval_banner.tag')}</Tag>
                <Box flexDirection="row" alignItems="center">
                  <Icon.Small icon="clock" />
                  <Text size="200">
                    {i18n.t('contract_versions.approval_banner.date_label', {
                      date: dateBackendToUI(requestedAt),
                    })}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
          <Button type="primary" onClick={navigateToApproval}>
            {i18n.t('contract_versions.approval_banner.view_request_button')}
          </Button>
        </Box>
      </CardInfo>
    </Box>
  )
}

export default observer(ApprovalBanner)
